import { useContext, useState } from 'react'
import { useFormikContext, Field } from 'formik'
import * as Yup from 'yup'
import { FormContent, FormHeader, InputField, ButtonDrawer } from '~/components'
import { StepperContext } from '~/components/ui/ViewStepper'
import { StoreContext } from '~/store'
import { wrapWithValidation } from '~/utils'
import { addName, completeActivation, isBadState } from '~/lib/api'
import { emitCustomEvent } from 'react-custom-events'
import { track } from '../../../utils/gtm'
import * as common from '~/config/common.module.css'
import * as MotionElement from '~/components/ui/MotionElement'
import { useTranslation } from 'react-i18next'
import i18next from '~/i18n'

const InputName = ({ fullscreen }) => {
  const { t } = useTranslation()
  const { reset, next, parentContext } = useContext(StepperContext)
  const { loginAndFetchBookings } = useContext(StoreContext)
  const { values, initialValues, setFieldValue, setFieldError } = useFormikContext()
  const [isLoading, setIsLoading] = useState(false)
  const [activationBadState, setActivationBadState] = useState(false)

  const wrappedReset = () => {
    for (const [key, value] of Object.entries(initialValues)) {
      setFieldValue(key, value, false)
    }
    reset()
  }

  const handleLoginComplete = (userId) => {
    loginAndFetchBookings(userId)
    if (fullscreen) {
      parentContext?.next('confirm')
    } else {
      emitCustomEvent('SHOW_LOGIN', false)
    }
  }

  const makeRequest = () => {
    setIsLoading(true)
    addName(values.firstName, values.lastName).then((response) => {
      const state = response?.deviceActivationState
      if (state === 'ACTIVATION_COMPLETE_NEW_USER' || state === 'ACTIVATION_COMPLETE_EXISTING_USER') {
        // Session already complete on server — login directly, do not call /complete
        if (response.shouldConsentToMarketing !== undefined) {
          setFieldValue('shouldConsentToMarketing', response.shouldConsentToMarketing, false)
        }
        if (response.shouldConsentToTermsAndConditions !== undefined) {
          setFieldValue('shouldConsentToTermsAndConditions', response.shouldConsentToTermsAndConditions, false)
        }
        if (response.shouldConsentToTermsAndConditions) {
          next('acceptTerms')
        } else if (response.userId) {
          track('confirmRegistration', { userId: response.userId })
          handleLoginComplete(response.userId)
        }
      } else if (state === 'NAME_ADDED') {
        completeActivation({ termsAndConditions: true })
          .then(activation => {
            if (activation.userId) {
              track('confirmRegistration', { userId: activation.userId })
              handleLoginComplete(activation.userId)
            }
          })
          .catch(error => setFieldError('firstName', error?.response?.message))
          .finally(() => setIsLoading(false))
        return
      }
    })
    .catch((error) => {
      console.error(error)
      setFieldError('firstName', error?.response?.message)
      if (isBadState(error)) {
        setActivationBadState(true)
      }
    })
    .finally(() => {
      setIsLoading(false)
    })
  }

  const wrappedNext = wrapWithValidation(makeRequest, useFormikContext())

  const buttons = [
    { func: wrappedReset, isBack: true },
    { func: wrappedNext, title: t('button-continue'), disabled: activationBadState, isLoading },
  ]

  return (
    <>
      <FormContent isLoading={isLoading} fullscreen={fullscreen}>
        <FormHeader>{t('header-enter-name')}</FormHeader>
        <MotionElement.div className={common.content}>
          <div className={common.fullWidth}>{t('info-driver-confirm-pickup')}</div>
          <Field name="firstName" placeholder={t('placeholder-firstname')} component={InputField} />
          <Field name="lastName" placeholder={t('placeholder-lastname')} component={InputField} />
        </MotionElement.div>
      </FormContent>
      <ButtonDrawer buttons={buttons} isLoading={isLoading} fullscreen={fullscreen} />
    </>
  )
}

InputName.validationSchema = Yup.object({
  firstName: Yup.string().required(i18next.t('validation-need-firstname')),
  lastName: Yup.string().required(i18next.t('validation-need-lastname')),
})

export default InputName
